// AICA Checkbox Component - Meridian Dark Design System
// Based on Radix UI Checkbox primitive
// Executive style: Professional teal with gold accents

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> {
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Error state
   */
  error?: boolean;
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size = 'md', error, ...props }, ref) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        // Base styles - Meridian Dark
        'peer shrink-0 rounded-sm border',
        'bg-bg-surface', // Surface background when unchecked
        'border-border', // Subtle border

        // Focus states - Meridian teal ring
        'focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-bg',

        // Checked state - Teal with gold accent
        'data-[state=checked]:bg-primary',
        'data-[state=checked]:border-primary',
        'data-[state=checked]:text-bg',
        'data-[state=checked]:shadow-glow-subtle',

        // Indeterminate state
        'data-[state=indeterminate]:bg-primary-dim',
        'data-[state=indeterminate]:border-primary',
        'data-[state=indeterminate]:text-bg',

        // Hover state
        'hover:border-border',
        'hover:shadow-sm',

        // Error state
        error && [
          'border-danger',
          'data-[state=checked]:bg-danger',
          'data-[state=checked]:border-danger',
          'focus-visible:ring-danger/30',
        ],

        // Disabled state
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
        'disabled:bg-bg-subtle',

        // Transitions
        'transition-all duration-150 ease-standard',
        'motion-reduce:transition-none',

        // Size variant
        sizes[size],

        className
      )}
      aria-invalid={error ? 'true' : undefined}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn(
          'flex items-center justify-center text-current',
          // Scale-in animation for check icon
          'data-[state=checked]:animate-in data-[state=checked]:zoom-in-95',
          'data-[state=unchecked]:animate-out data-[state=unchecked]:zoom-out-95',
          'data-[state=indeterminate]:animate-in data-[state=indeterminate]:zoom-in-95',
          'duration-150 ease-emphasized'
        )}
      >
        {props.checked === 'indeterminate' ? (
          <Minus className={iconSizes[size]} />
        ) : (
          <Check className={iconSizes[size]} strokeWidth={2.5} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;
